
import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { exportDescriptionsToCSV, exportDescriptionsToExcel } from "@/lib/api/export";
import { useUserSettings } from "@/hooks/useUserSettings";
import { useProductDetailsSettings } from "@/hooks/useProductDetailsSettings";

export const useCollectionExport = (collectionId: string) => {
  const { toast } = useToast();
  const { settings } = useUserSettings();
  const { data: productDetailsSettings } = useProductDetailsSettings();

  // Check if collection is fully approved
  const { data: isFullyApproved, isLoading: isCheckingApproval } = useQuery({
    queryKey: ["collectionApprovalStatus", collectionId],
    queryFn: async () => {
      if (!collectionId) return false;
      
      const { data: descriptions, error } = await supabase
        .from("product_descriptions")
        .select("status")
        .eq("collection_id", collectionId);

      if (error) throw error;

      if (!descriptions || descriptions.length === 0) return false;

      return !descriptions.some(d => d.status === "pending");
    },
    enabled: !!collectionId,
    refetchInterval: 2000, // Check every 2 seconds for status updates
  });

  const { mutate: exportCollection, isPending: isExporting } = useMutation({
    mutationFn: async () => {
      if (!isFullyApproved) {
        throw new Error("Cannot export: Not all descriptions are approved");
      }

      try {
        // Fetch all approved descriptions for the collection
        const { data, error } = await supabase
          .from("product_descriptions")
          .select(
            `
            id,
            original_text,
            generated_description,
            improved_description,
            article_number,
            product_details
            `
          )
          .eq("collection_id", collectionId)
          .eq("status", "approved")
          .order("article_number", { ascending: true })
          .order("created_at", { ascending: true });

        if (error) {
          throw error;
        }

        if (!data || data.length === 0) {
          toast({
            title: "No data to export",
            description: "This collection doesn't have any approved descriptions to export.",
            variant: "destructive",
          });
          return;
        }

        // Get collection name for the filename
        const { data: collection } = await supabase
          .from("collections")
          .select("name")
          .eq("id", collectionId)
          .single();

        const exportFormat = String(settings?.export_format || 'csv');
        const filePrefix = collection?.name || "collection";
        
        console.log("Exporting approved descriptions using format:", exportFormat);
        console.log("Export data count:", data.length);
        
        // Format the data for export
        const formattedDescriptions = data.map((desc) => ({
          originalText: desc.original_text || '',
          generatedDescription: desc.improved_description || desc.generated_description || '',
          articleNumber: desc.article_number || '',
          productDetails: desc.product_details || '',
          showProductDetails: productDetailsSettings?.showProductDetails || false
        }));
        
        // Export based on user preference
        if (exportFormat === 'excel') {
          exportDescriptionsToExcel(formattedDescriptions, filePrefix);
          toast({
            title: "Export successful!",
            description: `${data.length} approved descriptions exported as Excel file.`,
          });
        } else {
          exportDescriptionsToCSV(formattedDescriptions, filePrefix);
          toast({
            title: "Export successful!",
            description: `${data.length} approved descriptions exported as CSV file.`,
          });
        }
      } catch (error: any) {
        console.error("Export error:", error);
        throw error;
      }
    },
    onError: (error: any) => {
      toast({
        title: "Export failed",
        description: error.message || "Failed to export collection. Please try again.",
        variant: "destructive",
      });
    }
  });

  return {
    exportCollection,
    isExporting,
    isFullyApproved: isFullyApproved || false,
    isCheckingApproval,
  };
};
